import { useMutation, useQueries, useQuery } from '@tanstack/react-query'
import { ArrowLeft, ArrowRight, Minus, Plus, Trash2 } from 'lucide-react'
import { useId, useState, type KeyboardEvent, type ReactNode } from 'react'

import { variantDisplayName } from '@aeris/catalog'
import type {
  ExchangeDetail,
  ExchangeReasonCode,
  ExchangeSummary,
  ProductSummary,
  SalesDocumentDetail,
  SalesDocumentSummary,
} from '@aeris/contracts'
import { EXCHANGE_REASON_LABELS } from '@aeris/contracts'
import { reasonForLine, restocksFor, returnableQuantity } from '@aeris/exchange'
import {
  COMMERCIAL_ROUNDING,
  MONEY_SCALE,
  QUANTITY_SCALE,
  UNIT_PRICE_SCALE,
  add,
  compare,
  divide,
  formatMoney,
  fromString,
  lineTotal,
  parseHumanAmount,
  subtract,
  sum,
  toString as decimalToString,
  zero,
  type Decimal,
} from '@aeris/money'
import {
  Badge,
  Button,
  CheckboxField,
  Dialog,
  DialogContent,
  DialogFooter,
  EmptyState,
  Field,
  InlineAlert,
  Input,
  RadioGroup,
  SearchInput,
  Spinner,
  Stepper,
  Textarea,
  cn,
  loadFailureDescription,
} from '@aeris/ui'

import { createExchange } from '../lib/api/exchanges.ts'
import {
  ApiError,
  fetchProducts,
  fetchSalesDocument,
  fetchSalesDocuments,
} from '../lib/api.ts'
import { documentRef } from '../lib/sales-labels.ts'
import { timeAgo } from '../lib/time-ago.ts'
import { useDebounced } from '../lib/use-debounced.ts'

import { AuthorisationDialog } from './authorisation-dialog.tsx'
import {
  EXCHANGE_KIND_OPTIONS,
  KIND_CARDS_CLASS,
} from './exchange-kind-options.ts'
import { ExchangeReasonPicker } from './exchange-reason-picker.tsx'
import { PartyPicker } from './party-picker.tsx'

/**
 * A troca e o vale em etapas — F136 (o fluxo é o da F126, fatia 2b).
 *
 * O dono olhou o diálogo de uma tela só — tabela de cinco colunas com campos,
 * busca de produto, observação e totais empilhados — e disse "tá tudo quebrado,
 * ruim". O trocador faz sempre a mesma coisa, na mesma ordem, com o cliente na
 * frente: acha a venda, marca o que voltou, escolhe o que leva (se leva),
 * confere e conclui. Então são essas as etapas, uma de cada vez, com Enter
 * avançando e o resultado ("vira vale", "cliente paga") sempre à vista no
 * rodapé.
 *
 * ## O que a tela calcula e o que ela não decide
 *
 * Mesma regra de antes: a conta aqui é a de `@aeris/money`, igual à do
 * servidor, mas o preço que vale é o que o SERVIDOR lê — o que o cliente pagou
 * na venda para o que voltou, o da tabela padrão para o que sai. E a trava de
 * "não devolver mais que o vendido" é ajuda de tela: quem recusa de verdade é
 * `checkReturn`, que também desconta o que já voltou em trocas anteriores.
 */

type Step = 'sale' | 'returns' | 'outgoing' | 'review'

/**
 * A peça que voltou.
 *
 * Se ela volta ao estoque quem diz é o motivo (F175, `restocksFor`): defeito
 * não volta; tamanho ou cor, não serviu e arrependimento voltam. `restock` é a
 * escolha da pessoa, e só conta no motivo "Outro".
 */
type ReturnDraft = {
  quantity: string
  note: string
  noteOpen: boolean
  restock: boolean
  /**
   * Por que ESTA peça voltou (F178). Nulo = o motivo da troca inteira.
   *
   * A nota com duas telas — uma com defeito, outra que não serviu — precisa
   * dos dois, porque é o motivo que decide se a peça volta ao estoque.
   */
  reasonCode: ExchangeReasonCode | null
}

/** Uma cor de um produto, como a busca da troca a oferece (F176). */
type ProductOption = {
  readonly key: string
  readonly product: ProductSummary
  readonly variant: ProductSummary['variants'][number]
  readonly label: string
}

type OutgoingDraft = {
  variantId: string
  name: string
  unit: string
  unitPrice: string | null
  quantity: string
}

const ONE = fromString('1', QUANTITY_SCALE)

/** Oito é o que cabe no diálogo sem rolar. "Mostrar mais" traz as próximas. */
const SALE_PAGE_SIZE = 8

const quantityText = (wire: string): string =>
  wire.replace(/\.?0+$/, '').replace('.', ',')

const shortDate = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' })

const money = (wire: string, scale = MONEY_SCALE): string =>
  formatMoney(fromString(wire, scale))

/**
 * − quantidade +. O campo continua digitável: 0,5 kg não se chega apertando +.
 */
const QuantityStepper = ({
  label,
  value,
  max,
  onChange,
}: {
  label: string
  value: string
  /** Nulo quando não há teto na tela (o que o cliente leva). */
  max: Decimal | null
  onChange: (next: string) => void
}): ReactNode => {
  const current =
    value.trim() === ''
      ? zero(QUANTITY_SCALE)
      : parseHumanAmount(value, QUANTITY_SCALE)

  const step = (direction: 1 | -1): void => {
    const base = current ?? zero(QUANTITY_SCALE)
    let next = direction === 1 ? add(base, ONE) : subtract(base, ONE)
    if (next.units < 0n) next = zero(QUANTITY_SCALE)
    if (max !== null && compare(next, max) > 0) next = max
    onChange(next.units === 0n ? '' : quantityText(decimalToString(next)))
  }

  return (
    <div role="group" aria-label={label} className="flex items-center gap-1">
      <Button
        type="button"
        variant="secondary"
        size="icon"
        aria-label={`Menos: ${label}`}
        disabled={current === null || current.units <= 0n}
        onClick={() => {
          step(-1)
        }}
        leadingIcon={<Minus aria-hidden="true" className="h-3.5 w-3.5" />}
      />
      <Input
        className="w-16 text-center font-(--font-mono)"
        inputMode="decimal"
        aria-label={label}
        value={value}
        placeholder="0"
        onChange={(event) => {
          onChange(event.target.value)
        }}
      />
      <Button
        type="button"
        variant="secondary"
        size="icon"
        aria-label={`Mais: ${label}`}
        disabled={
          max !== null && current !== null && compare(current, max) >= 0
        }
        onClick={() => {
          step(1)
        }}
        leadingIcon={<Plus aria-hidden="true" className="h-3.5 w-3.5" />}
      />
    </div>
  )
}

/** ↑/↓ andam numa lista de resultados presa a um campo de busca. */
const useHighlight = (count: number) => {
  const [index, setIndex] = useState(0)
  const safe = count === 0 ? -1 : Math.min(index, count - 1)
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (count === 0) return
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setIndex((current) => Math.min(current + 1, count - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setIndex((current) => Math.max(current - 1, 0))
    }
  }
  return { index: safe, setIndex, onKeyDown }
}

export const NewExchangeDialog = ({
  request = null,
  onDone,
  onClose,
}: {
  /**
   * O pedido do caixa que esta troca atende (fatia 2d). Abre já na venda e no
   * tipo que o caixa pediu, na etapa das peças, e conclui o MESMO registro.
   */
  request?: ExchangeSummary | null
  onDone: (exchange: ExchangeDetail) => void
  onClose: () => void
}): ReactNode => {
  const saleListId = useId()
  const productListId = useId()

  const [step, setStep] = useState<Step>(request === null ? 'sale' : 'returns')
  const [saleSearch, setSaleSearch] = useState('')
  /**
   * O cliente cujas vendas a lista mostra (F183).
   *
   * O dono: "quando escolhe o cliente pra ver troca/vale tem que mostrar TODAS
   * as vendas, mostra só umas, o usuário não acha". Pelo nome digitado a lista
   * trazia as oito mais recentes de TODOS os clientes cujo nome casava — e nada
   * na tela dizia que havia mais. Escolhido o cliente, a lista é a dele, e ela
   * pagina até acabar.
   */
  const [customerPartyId, setCustomerPartyId] = useState('')
  /** Quantas páginas de vendas já foram pedidas. "Mostrar mais" soma uma. */
  const [salePages, setSalePages] = useState(1)
  const [saleId, setSaleId] = useState<string | null>(
    request?.originalSaleId ?? null
  )
  const [kind, setKind] = useState<'exchange' | 'voucher'>(
    request?.kind ?? 'voucher'
  )
  const [returns, setReturns] = useState<Record<string, ReturnDraft>>({})
  const [productSearch, setProductSearch] = useState('')
  const [outgoing, setOutgoing] = useState<OutgoingDraft[]>([])
  /*
   * Por que a peça voltou (F158).
   *
   * Começa com o que o CAIXA escolheu, quando esta troca conclui um pedido —
   * o trocador confere em vez de responder de novo —, e ele pode corrigir: é
   * ele quem tem a peça na mão.
   */
  const [reasonCode, setReasonCode] = useState<ExchangeReasonCode | null>(
    request?.reasonCode ?? null
  )
  const [note, setNote] = useState('')
  /*
   * A liberação do gerente para devolver de novo uma peça já trocada (F178).
   *
   * Guardadas juntas porque só valem juntas: a senha é para ESTA venda e para
   * ESTAS linhas, e o servidor confere as duas coisas. Trocar de venda limpa
   * as duas, em `chooseSale`.
   */
  const [repeatAuthorisationId, setRepeatAuthorisationId] = useState<
    string | null
  >(null)
  const [releasedLines, setReleasedLines] = useState<readonly string[]>([])
  /** A peça cujo "Liberar mesmo assim" está pedindo a senha do gerente. */
  const [releasing, setReleasing] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  /** A venda da lista com os itens abertos pelo "Ver" (F145). */
  const [peekId, setPeekId] = useState<string | null>(null)

  const saleTerm = useDebounced(saleSearch.trim())
  const productTerm = useDebounced(productSearch.trim())

  /**
   * A lista de vendas procura quando há texto OU cliente escolhido.
   *
   * Com o cliente escolhido e o campo vazio a busca É legítima: são todas as
   * vendas dele. Antes, campo vazio era "não procure nada".
   */
  const searchingSales =
    step === 'sale' && (saleTerm.length > 0 || customerPartyId !== '')

  /*
   * Uma consulta por página, e não uma página que cresce (F183).
   *
   * `pageSize` cresceria até o teto de 100 do contrato — e um teto calado é
   * exatamente o defeito que esta feature conserta. Cada página é uma chave de
   * cache própria: "Mostrar mais" não rebusca as anteriores, e voltar da etapa
   * seguinte reencontra a lista inteira montada.
   */
  const salePageQueries = useQueries({
    queries: Array.from({ length: salePages }, (_, index) => ({
      queryKey: [
        'exchange-sale-search',
        saleTerm,
        customerPartyId,
        index + 1,
      ] as const,
      queryFn: async ({ signal }: { signal: AbortSignal }) =>
        /*
         * Quem decide o que é trocável é o SERVIDOR (F159).
         *
         * Aqui havia `kind: 'sale', status: 'completed'` — uma das quatro
         * cópias da regra. Ela deixava de fora todo pedido entregue, que é
         * quase tudo o que a loja vende; e, quando o dono mudou a regra,
         * mudá-la aqui não mudaria nas outras três.
         */
        await fetchSalesDocuments(
          {
            exchangeable: true,
            search: saleTerm,
            ...(customerPartyId === '' ? {} : { customerPartyId }),
            page: index + 1,
            pageSize: SALE_PAGE_SIZE,
          },
          signal
        ),
      enabled: searchingSales,
    })),
  })
  const saleResults: readonly SalesDocumentSummary[] = salePageQueries.flatMap(
    (page) => page.data?.items ?? []
  )
  /** Quantas o filtro tem ao todo — o número que faltava na tela. */
  const saleTotal = salePageQueries[0]?.data?.total ?? 0
  const salesError = salePageQueries.find((page) => page.isError)
  /*
   * Só a PRIMEIRA página esconde a lista.
   *
   * Se "Mostrar mais" trocasse a lista por "Buscando vendas…", clicar no botão
   * apagaria o que a pessoa estava lendo. As seguintes giram no próprio botão.
   */
  const salesPending = salePageQueries[0]?.isPending ?? false
  const loadingMoreSales = salePageQueries
    .slice(1)
    .some((page) => page.isPending)
  const saleHighlight = useHighlight(saleResults.length)

  const sale = useQuery({
    queryKey: ['sales-document', saleId],
    queryFn: async ({ signal }) =>
      await fetchSalesDocument(saleId ?? '', signal),
    enabled: saleId !== null,
  })
  const document: SalesDocumentDetail | undefined = sale.data?.document

  /*
   * Os itens de uma venda da lista, antes de escolhê-la (F145).
   *
   * A mesma chave da venda escolhida: quem olha e depois escolhe não busca de
   * novo.
   */
  const peek = useQuery({
    queryKey: ['sales-document', peekId],
    queryFn: async ({ signal }) =>
      await fetchSalesDocument(peekId ?? '', signal),
    enabled: step === 'sale' && peekId !== null,
  })

  const products = useQuery({
    queryKey: ['exchange-product-search', productTerm],
    queryFn: async ({ signal }) =>
      await fetchProducts(
        {
          search: productTerm,
          status: 'active',
          page: 1,
          pageSize: 8,
          include: ['price'],
        },
        signal
      ),
    enabled: step === 'outgoing' && productTerm.length > 1,
  })
  /*
   * Uma linha por cor (F176): o cliente troca a capa preta pela azul, e o que
   * sai do estoque é a azul. Produto sem cores é uma linha, como sempre.
   */
  const productResults: readonly ProductOption[] = (
    products.data?.items ?? []
  ).flatMap((product) =>
    product.variants
      // A API só manda as ativas; a arquivada sairia daqui de todo jeito.
      .filter((variant) => variant.status !== 'archived')
      .map((variant) => ({
        key: variant.id,
        product,
        variant,
        label: variantDisplayName(product.name, variant.name),
      }))
  )
  const productHighlight = useHighlight(productResults.length)

  /*
   * O que o cliente PAGOU por unidade — total da linha ÷ quantidade vendida.
   *
   * A mesma conta do servidor. Usar o preço de tabela da linha prometeria na
   * tela, numa venda com desconto, um vale maior do que o comprovante imprime.
   */
  const paidUnitPrice = (line: SalesDocumentDetail['lines'][number]) =>
    divide(
      fromString(line.lineTotal, MONEY_SCALE),
      fromString(line.quantity, QUANTITY_SCALE),
      UNIT_PRICE_SCALE,
      COMMERCIAL_ROUNDING
    )

  const lines = document?.lines ?? []
  const draftOf = (lineId: string): ReturnDraft =>
    returns[lineId] ?? {
      quantity: '',
      note: '',
      noteOpen: false,
      restock: false,
      reasonCode: null,
    }
  /** O motivo que vale para a peça: o dela, ou o da troca (F178). */
  const reasonOf = (draft: ReturnDraft): ExchangeReasonCode | null =>
    reasonCode === null ? null : reasonForLine(reasonCode, draft.reasonCode)
  /** O que acontece com a peça, pelo motivo dela (F175, F178). */
  const backToStock = (draft: ReturnDraft): boolean => {
    const reason = reasonOf(draft)
    return reason !== null && restocksFor(reason, draft.restock)
  }
  const patchLine = (lineId: string, next: Partial<ReturnDraft>): void => {
    setReturns((current) => ({
      ...current,
      [lineId]: { ...draftOf(lineId), ...current[lineId], ...next },
    }))
  }

  const returnChecks = lines.map((line) => {
    const draft = draftOf(line.id)
    const sold = fromString(line.quantity, QUANTITY_SCALE)
    /*
     * O que já voltou desta peça, e o que ainda pode voltar (F178).
     *
     * O servidor sempre recusou a segunda devolução da mesma peça; o que
     * faltava era a tela saber ANTES, em vez de o trocador marcar, escolher o
     * que o cliente leva e descobrir no fim. Liberada pelo gerente, a peça
     * volta a valer pelo que foi vendido — a trava que ele atravessou é
     * exatamente esta.
     */
    const returned = fromString(line.returnedQuantity, QUANTITY_SCALE)
    const released = releasedLines.includes(line.id)
    /*
     * O que virou vale não volta de novo, nem com o gerente (F257): o
     * cliente saiu com crédito, não com outra peça. O gerente reabre só o
     * que voltou numa troca.
     */
    const repeatable = returnableQuantity(
      sold,
      fromString(line.returnedAsVoucherQuantity, QUANTITY_SCALE)
    )
    const returnable = released
      ? repeatable
      : returnableQuantity(sold, returned)
    const quantity =
      draft.quantity.trim() === ''
        ? zero(QUANTITY_SCALE)
        : parseHumanAmount(draft.quantity, QUANTITY_SCALE)
    return {
      line,
      draft,
      sold,
      returned,
      returnable,
      released,
      /** Já voltou tudo: só o gerente reabre. */
      exhausted: returnable.units <= 0n,
      /** Já voltou tudo e virou vale: nem o gerente reabre (F257). */
      closed: returnable.units <= 0n && repeatable.units <= 0n,
      quantity,
      invalid: quantity === null || quantity.units < 0n,
      overSold: quantity !== null && compare(quantity, returnable) > 0,
    }
  })
  const returnedLines = returnChecks.flatMap((entry) =>
    entry.quantity === null || entry.quantity.units <= 0n
      ? []
      : [{ ...entry, quantity: entry.quantity }]
  )

  const outgoingChecks = outgoing.map((item) => ({
    item,
    quantity: parseHumanAmount(item.quantity, QUANTITY_SCALE),
  }))

  /*
   * Na troca, o cliente leva no máximo o que devolveu (F145).
   *
   * Voltou 1, leva 1: o que passa disso é venda, e venda é do caixa. O
   * servidor recusa com `outgoing-exceeds-returned`; aqui é o + que para e o
   * bipe que avisa antes. Peça mais cara na mesma quantidade continua valendo.
   */
  const returnedQuantity = sum(
    returnedLines.map((entry) => entry.quantity),
    QUANTITY_SCALE
  )
  const outgoingQuantity = sum(
    outgoingChecks.flatMap(({ quantity }) =>
      quantity === null || quantity.units <= 0n ? [] : [quantity]
    ),
    QUANTITY_SCALE
  )
  const overTaken = compare(outgoingQuantity, returnedQuantity) > 0
  const limitText = `Na troca o cliente leva no máximo ${quantityText(decimalToString(returnedQuantity))} ${compare(returnedQuantity, ONE) === 0 ? 'peça' : 'peças'}. Mais que isso é venda, e venda é no caixa.`

  /** Quanto este item ainda pode subir sem passar do que voltou. */
  const roomFor = (item: OutgoingDraft): Decimal => {
    const own = parseHumanAmount(item.quantity, QUANTITY_SCALE)
    const others = subtract(
      outgoingQuantity,
      own === null || own.units <= 0n ? zero(QUANTITY_SCALE) : own
    )
    const room = subtract(returnedQuantity, others)
    return room.units < 0n ? zero(QUANTITY_SCALE) : room
  }

  const returnedTotal = sum(
    returnedLines.map((entry) =>
      lineTotal(entry.quantity, paidUnitPrice(entry.line))
    ),
    MONEY_SCALE
  )
  const outgoingTotal = sum(
    outgoingChecks.flatMap(({ item, quantity }) =>
      quantity === null || item.unitPrice === null
        ? []
        : [lineTotal(quantity, fromString(item.unitPrice, UNIT_PRICE_SCALE))]
    ),
    MONEY_SCALE
  )
  const difference =
    kind === 'voucher'
      ? subtract(zero(MONEY_SCALE), returnedTotal)
      : subtract(outgoingTotal, returnedTotal)

  const steps: readonly { id: Step; label: string }[] = [
    { id: 'sale', label: 'Venda' },
    { id: 'returns', label: 'O que volta' },
    ...(kind === 'exchange'
      ? [{ id: 'outgoing' as const, label: 'O que leva' }]
      : []),
    { id: 'review', label: 'Conferir' },
  ]
  const stepIndex = Math.max(
    steps.findIndex((entry) => entry.id === step),
    0
  )

  /*
   * Por que o Continuar está apagado — escrito, e não só apagado.
   *
   * Um botão cinza sem motivo é a pessoa clicando três vezes e chamando alguém.
   */
  const blocker = ((): string | null => {
    switch (step) {
      case 'sale':
        return document === undefined ? 'Escolha a venda.' : null
      case 'returns':
        if (returnChecks.some((entry) => entry.invalid)) {
          return 'Tem quantidade que não é número.'
        }
        if (returnChecks.some((entry) => entry.overSold)) {
          return 'Uma peça passa da quantidade vendida.'
        }
        if (returnedLines.length === 0) {
          return 'Marque ao menos uma peça que voltou.'
        }
        /*
         * A pergunta que faltava, e que explica os 91% (F158).
         *
         * A troca criada pelo trocador nunca gravou motivo — ninguém nunca foi
         * perguntado. Ela mora na etapa "O que volta" porque é aqui que ele
         * está com a peça na mão, e não na revisão, onde vira um obstáculo
         * entre a pessoa e o botão de concluir.
         */
        return reasonCode === null ? 'Diga por que a peça voltou.' : null
      case 'outgoing':
        if (outgoing.length === 0) return 'Adicione o que o cliente leva.'
        if (
          outgoingChecks.some(
            ({ quantity }) => quantity === null || quantity.units <= 0n
          )
        ) {
          return 'Tem produto sem quantidade.'
        }
        if (overTaken) return limitText
        return outgoing.some((item) => item.unitPrice === null)
          ? 'Tem produto sem preço: tire da lista ou cadastre o preço.'
          : null
      case 'review':
        return null
    }
  })()

  const create = useMutation({
    mutationFn: async () => {
      if (document === undefined) {
        throw new ApiError(409, 'no_sale', 'Escolha a venda.')
      }
      return await createExchange({
        kind,
        originalSaleId: document.id,
        returned: returnedLines.map((entry) => ({
          saleLineId: entry.line.id,
          quantity: decimalToString(entry.quantity),
          // O servidor decide de novo pelo motivo; isto é o que a tela mostrou.
          restock: backToStock(entry.draft),
          note: entry.draft.note.trim() === '' ? null : entry.draft.note.trim(),
          /*
           * Com mais de uma peça, cada uma manda o motivo que a tela mostra
           * nela — o próprio ou o da troca, que aparece já marcado (F188: o
           * servidor recusa peça sem motivo). Com uma, o da troca responde
           * por ela, e o da peça só viaja quando difere (F178).
           */
          ...(returnedLines.length > 1
            ? { reasonCode: reasonOf(entry.draft) ?? 'outro' }
            : entry.draft.reasonCode === null
              ? {}
              : { reasonCode: entry.draft.reasonCode }),
        })),
        outgoing:
          kind === 'voucher'
            ? []
            : outgoingChecks.flatMap(({ item, quantity }) =>
                quantity === null
                  ? []
                  : [
                      {
                        variantId: item.variantId,
                        quantity: decimalToString(quantity),
                      },
                    ]
              ),
        // Não-nulo: o `blocker` da etapa "O que volta" não deixa passar.
        reasonCode: reasonCode ?? 'outro',
        note: note.trim() === '' ? null : note.trim(),
        ...(request === null ? {} : { requestId: request.id }),
        // A senha do gerente, quando alguma peça já tinha voltado (F178).
        ...(repeatAuthorisationId === null ? {} : { repeatAuthorisationId }),
      })
    },
    onSuccess: (response) => {
      onDone(response.exchange)
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível concluir a troca.'
      )
    },
  })

  const chooseSale = (picked: SalesDocumentSummary): void => {
    /*
     * A cancelada aparece, mas não se escolhe (F161).
     *
     * O dono quer vê-la na busca, "aparecendo que está cancelada", para o
     * trocador não achar que digitou o número errado. Escolher não leva a
     * lugar nenhum: o servidor recusaria a troca no fim.
     */
    if (picked.exchangeScope !== 'allowed') {
      setError(
        picked.status === 'cancelled'
          ? `${documentRef(picked.kind, picked.number)} foi cancelado(a): não dá para trocar nem gerar vale.`
          : 'Este documento não pode ser trocado.'
      )
      return
    }
    setSaleId(picked.id)
    setReturns({})
    setOutgoing([])
    /* A senha do gerente era para a venda anterior (F178). */
    setRepeatAuthorisationId(null)
    setReleasedLines([])
    setStep('returns')
  }

  const addProduct = ({ product, variant, label }: ProductOption): void => {
    if (compare(add(outgoingQuantity, ONE), returnedQuantity) > 0) {
      setError(limitText)
      setProductSearch('')
      return
    }
    setOutgoing((current) => {
      const existing = current.find((item) => item.variantId === variant.id)
      if (existing !== undefined) {
        // O mesmo produto de novo é "mais um", como o bipe do PDV.
        const quantity =
          parseHumanAmount(existing.quantity, QUANTITY_SCALE) ??
          zero(QUANTITY_SCALE)
        return current.map((item) =>
          item.variantId === variant.id
            ? {
                ...item,
                quantity: quantityText(decimalToString(add(quantity, ONE))),
              }
            : item
        )
      }
      return [
        ...current,
        {
          variantId: variant.id,
          name: label,
          unit: variant.unit,
          unitPrice: product.price?.basePrice ?? null,
          quantity: '1',
        },
      ]
    })
    setProductSearch('')
  }

  /** Código de barras ou SKU exato: o bipe do leitor adiciona sem escolher. */
  const exactProduct = (): ProductOption | undefined => {
    const typed = productSearch.trim().toLowerCase()
    if (typed === '') return undefined
    return productResults.find(
      ({ variant }) =>
        variant.sku.toLowerCase() === typed ||
        (variant.gtin ?? '').toLowerCase() === typed
    )
  }

  const next = (): void => {
    const following = steps[stepIndex + 1]
    if (following !== undefined) setStep(following.id)
  }

  const back = (): void => {
    const previous = steps[stepIndex - 1]
    if (previous === undefined) return
    if (previous.id === 'sale' && request !== null) return
    setStep(previous.id)
  }

  /*
   * Enter faz a coisa óbvia da etapa: na busca de venda, abre a destacada; na
   * busca de produto, adiciona (o exato primeiro); nas outras, avança; no fim,
   * conclui.
   */
  const onSubmit = (): void => {
    setError(null)
    if (step === 'sale') {
      const picked = saleResults[saleHighlight.index]
      if (picked !== undefined) chooseSale(picked)
      else if (document !== undefined) next()
      return
    }
    if (step === 'outgoing' && productSearch.trim() !== '') {
      const picked = exactProduct() ?? productResults[productHighlight.index]
      if (picked !== undefined) addProduct(picked)
      return
    }
    if (blocker !== null) return
    if (step === 'review') {
      create.mutate()
      return
    }
    next()
  }

  const resultTone =
    difference.units < 0n
      ? 'text-(--success)'
      : difference.units > 0n
        ? 'text-(--warning)'
        : 'text-(--text-primary)'
  const resultText =
    difference.units < 0n
      ? `Vira vale ${formatMoney(subtract(zero(MONEY_SCALE), difference))}`
      : difference.units > 0n
        ? `Cliente paga ${formatMoney(difference)} no caixa`
        : 'Sem diferença'

  const kindLabel = (value: 'exchange' | 'voucher'): string =>
    value === 'voucher' ? 'Vale' : 'Troca por outra peça'

  return (
    <>
      <Dialog open onOpenChange={onClose}>
        <DialogContent
          title={
            request === null ? 'Nova troca ou vale' : 'Atender pedido de troca'
          }
          description="Só se troca o que o Aeris vendeu: comece pela venda."
          className="max-w-3xl"
        >
          <form
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault()
              onSubmit()
            }}
          >
            <Stepper
              steps={steps}
              currentIndex={stepIndex}
              onStepClick={(index) => {
                const target = steps[index]
                if (target === undefined) return
                if (target.id === 'sale' && request !== null) return
                setStep(target.id)
              }}
            />

            {request === null ? null : (
              <InlineAlert
                tone="info"
                title={`Pedido de ${request.requestedByName ?? 'caixa'} · ${timeAgo(request.requestedAt ?? request.createdAt)}`}
              >
                {`Quer ${kindLabel(request.kind).toLowerCase()}`}
                {request.reasonCode === null
                  ? ''
                  : ` · ${EXCHANGE_REASON_LABELS[request.reasonCode]}`}
                {request.reason === null ? '' : ` · “${request.reason}”`}
              </InlineAlert>
            )}

            {/* 1. A venda */}
            {step === 'sale' ? (
              <section className="flex flex-col gap-3">
                <Field
                  label="Número da venda ou nome do cliente"
                  hint="↑ e ↓ escolhem, Enter abre a venda."
                >
                  {(field) => (
                    <SearchInput
                      {...field}
                      autoFocus
                      wrapperClassName="min-w-0"
                      value={saleSearch}
                      placeholder="ex.: 1234 ou Maria"
                      role="combobox"
                      aria-expanded={saleResults.length > 0}
                      aria-controls={saleListId}
                      aria-activedescendant={
                        saleHighlight.index < 0
                          ? undefined
                          : `${saleListId}-${String(saleHighlight.index)}`
                      }
                      onKeyDown={saleHighlight.onKeyDown}
                      onChange={(event) => {
                        setSaleSearch(event.target.value)
                        saleHighlight.setIndex(0)
                        setSalePages(1)
                      }}
                    />
                  )}
                </Field>

                {/*
                 * O caminho do cliente (F183).
                 *
                 * O mesmo `PartyPicker` da troca sem venda — que já acha pelo
                 * nome, pelo documento e pelo código do sistema antigo. Vale
                 * junto com o texto: escolher a Maria e digitar 1234 é "a venda
                 * 1234 da Maria".
                 */}
                <Field
                  label="Ou escolha o cliente"
                  hint="Mostra todas as vendas dele, da mais recente para a mais antiga."
                >
                  {(field) => (
                    <PartyPicker
                      {...field}
                      role="customer"
                      value={customerPartyId}
                      placeholder="Nome, documento ou código do cliente"
                      onChange={(partyId) => {
                        setCustomerPartyId(partyId)
                        saleHighlight.setIndex(0)
                        setSalePages(1)
                      }}
                    />
                  )}
                </Field>

                {document !== undefined && !searchingSales ? (
                  <InlineAlert tone="info">
                    {`${documentRef(document.kind, document.number)} · ${document.customerName} já escolhida. Enter continua.`}
                  </InlineAlert>
                ) : null}

                {!searchingSales ? null : salesError !== undefined ? (
                  <InlineAlert
                    tone="danger"
                    title="Não foi possível buscar as vendas."
                  >
                    {loadFailureDescription(salesError.error)}
                  </InlineAlert>
                ) : salesPending ? (
                  <p className="flex items-center gap-2 text-(length:--text-sm) text-(--text-muted)">
                    <Spinner /> Buscando vendas…
                  </p>
                ) : saleResults.length === 0 ? (
                  <EmptyState
                    title={
                      saleTerm === ''
                        ? 'Este cliente não tem venda trocável'
                        : `Nenhuma venda trocável com “${saleTerm}”`
                    }
                    /*
                     * A segunda frase diz a causa mais provável (F159).
                     *
                     * A venda pode existir e não estar aqui: entrega ainda na rua,
                     * ou Consumidor Final. Sem isso, o trocador conclui que digitou
                     * errado e procura o número três vezes.
                     */
                    description="Confira o número do cupom ou o nome do cliente. Entrega que o entregador ainda não acertou, e venda para o Consumidor Final, não aparecem aqui."
                  />
                ) : (
                  <>
                    <ul
                      id={saleListId}
                      role="listbox"
                      aria-label="Vendas encontradas"
                      className="flex flex-col gap-1"
                    >
                      {saleResults.map((item, index) => {
                        const open = peekId === item.id
                        const label = documentRef(item.kind, item.number)
                        const blocked = item.exchangeScope !== 'allowed'
                        const panelId = `${saleListId}-itens-${String(index)}`
                        return (
                          <li
                            key={item.id}
                            role="presentation"
                            className={cn(
                              'flex flex-col rounded-(--radius-md) border',
                              index === saleHighlight.index
                                ? 'border-(--brand-primary) bg-(--surface-sunken)'
                                : 'border-(--border-default)'
                            )}
                          >
                            <div className="flex items-center gap-2 pr-2">
                              <div
                                id={`${saleListId}-${String(index)}`}
                                role="option"
                                aria-selected={index === saleHighlight.index}
                                aria-disabled={blocked}
                                onMouseEnter={() => {
                                  saleHighlight.setIndex(index)
                                }}
                                onClick={() => {
                                  chooseSale(item)
                                }}
                                className={cn(
                                  'flex min-w-0 flex-1 items-center justify-between gap-3 px-3 py-2',
                                  blocked
                                    ? 'cursor-not-allowed opacity-70'
                                    : 'cursor-pointer'
                                )}
                              >
                                <span className="flex min-w-0 flex-col">
                                  <span className="flex min-w-0 items-center gap-2">
                                    <span
                                      className={cn(
                                        'truncate font-(--weight-medium)',
                                        blocked && 'line-through'
                                      )}
                                    >
                                      {`${label} · ${item.customerName}`}
                                    </span>
                                    {item.status === 'cancelled' ? (
                                      <Badge tone="danger">Cancelada</Badge>
                                    ) : null}
                                  </span>
                                  <span className="text-(length:--text-xs) text-(--text-muted)">
                                    {shortDate.format(new Date(item.issuedAt))}
                                    {item.salespersonName === null
                                      ? ''
                                      : ` · vendeu ${item.salespersonName}`}
                                  </span>
                                </span>
                                <span className="shrink-0 font-(--font-mono) tabular-nums">
                                  {money(item.totalAmount)}
                                </span>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                aria-expanded={open}
                                aria-controls={panelId}
                                aria-label={`${open ? 'Esconder' : 'Ver'} os itens da ${label}`}
                                onClick={() => {
                                  setPeekId(open ? null : item.id)
                                }}
                              >
                                {open ? 'Esconder' : 'Ver'}
                              </Button>
                            </div>
                            {open ? (
                              <div
                                id={panelId}
                                className="border-t border-(--border-default) px-3 py-2 text-(length:--text-sm)"
                              >
                                {peek.isError ? (
                                  <InlineAlert
                                    tone="danger"
                                    title="Não foi possível abrir os itens."
                                  >
                                    {loadFailureDescription(peek.error)}
                                  </InlineAlert>
                                ) : peek.data === undefined ? (
                                  <p className="flex items-center gap-2 text-(--text-muted)">
                                    <Spinner /> Carregando os itens…
                                  </p>
                                ) : (
                                  <ul
                                    aria-label={`Itens da ${label}`}
                                    className="flex flex-col gap-1"
                                  >
                                    {peek.data.document.lines.map((line) => (
                                      <li
                                        key={line.id}
                                        className="flex justify-between gap-2"
                                      >
                                        <span className="min-w-0">
                                          {`${quantityText(line.quantity)} × ${line.description}`}
                                        </span>
                                        <span className="shrink-0 font-(--font-mono) tabular-nums">
                                          {money(line.lineTotal)}
                                        </span>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            ) : null}
                          </li>
                        )
                      })}
                    </ul>

                    {/*
                     * Quantas há ao todo, e como ver o resto (F183).
                     *
                     * Um resultado cortado sem dizer que foi cortado é o que
                     * produziu a reclamação: o atendente via oito, não achava a
                     * venda e concluía que ela não existia.
                     */}
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span
                        aria-live="polite"
                        className="text-(length:--text-xs) text-(--text-muted)"
                      >
                        {`Mostrando ${String(saleResults.length)} de ${String(saleTotal)} ${saleTotal === 1 ? 'venda' : 'vendas'}`}
                      </span>
                      {saleResults.length < saleTotal ? (
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          loading={loadingMoreSales}
                          onClick={() => {
                            setSalePages((current) => current + 1)
                          }}
                        >
                          Mostrar mais
                        </Button>
                      ) : null}
                    </div>
                  </>
                )}
              </section>
            ) : null}

            {step !== 'sale' && document === undefined ? (
              sale.isError ? (
                <InlineAlert tone="danger">
                  Não foi possível abrir a venda.
                </InlineAlert>
              ) : (
                <p className="flex items-center gap-2 text-(length:--text-sm) text-(--text-muted)">
                  <Spinner /> Abrindo a venda…
                </p>
              )
            ) : null}

            {step !== 'sale' && document !== undefined ? (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-(--radius-md) bg-(--surface-sunken) px-3 py-2 text-(length:--text-sm)">
                <span>
                  <strong>{documentRef(document.kind, document.number)}</strong>{' '}
                  · {document.customerName} ·{' '}
                  {shortDate.format(new Date(document.issuedAt))} ·{' '}
                  {money(document.totalAmount)}
                </span>
                {request === null ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setStep('sale')
                    }}
                  >
                    Trocar de venda
                  </Button>
                ) : null}
              </div>
            ) : null}

            {/* 2. O que volta */}
            {step === 'returns' && document !== undefined ? (
              <section className="flex flex-col gap-3">
                <Field label="O cliente quer">
                  {(field) => (
                    <RadioGroup
                      id={field.id}
                      name="exchange-kind"
                      orientation="horizontal"
                      value={kind}
                      onValueChange={setKind}
                      options={EXCHANGE_KIND_OPTIONS}
                      className={KIND_CARDS_CLASS}
                    />
                  )}
                </Field>

                <Field
                  label="Por que voltou"
                  hint={
                    request?.reasonCode == null
                      ? undefined
                      : 'O caixa já respondeu. Corrija se a peça disser outra coisa.'
                  }
                >
                  {() => (
                    <ExchangeReasonPicker
                      value={reasonCode}
                      onValueChange={setReasonCode}
                    />
                  )}
                </Field>

                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-(length:--text-sm) font-(--weight-semibold)">
                    O que voltou
                  </h3>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setReturns((current) => {
                        const all: Record<string, ReturnDraft> = { ...current }
                        for (const line of lines) {
                          all[line.id] = {
                            ...draftOf(line.id),
                            ...current[line.id],
                            quantity: quantityText(line.quantity),
                          }
                        }
                        return all
                      })
                    }}
                  >
                    Devolver tudo
                  </Button>
                </div>

                <ul className="flex flex-col gap-2">
                  {returnChecks.map(
                    ({
                      line,
                      draft,
                      returned,
                      returnable,
                      released,
                      exhausted,
                      closed,
                      quantity,
                      overSold,
                    }) => {
                      const marked = quantity !== null && quantity.units > 0n
                      const lineReason = reasonOf(draft)
                      return (
                        <li
                          key={line.id}
                          className={cn(
                            'flex flex-col gap-3 rounded-(--radius-md) border p-3',
                            overSold
                              ? 'border-(--danger)'
                              : marked
                                ? 'border-(--brand-primary)'
                                : 'border-(--border-default)'
                          )}
                        >
                          <div className="flex flex-wrap items-center justify-between gap-3">
                            <div className="flex min-w-0 flex-col">
                              <span
                                className={cn(
                                  'font-(--weight-medium)',
                                  exhausted ? 'line-through' : undefined
                                )}
                              >
                                {line.description}
                              </span>
                              <span className="text-(length:--text-xs) text-(--text-muted)">
                                {`pago ${formatMoney(paidUnitPrice(line))} cada · vendido ${quantityText(line.quantity)}`}
                                {returned.units > 0n
                                  ? ` · já voltaram ${quantityText(decimalToString(returned))}`
                                  : ''}
                              </span>
                            </div>
                            {closed ? (
                              <Badge tone="neutral">
                                {`virou vale (${quantityText(decimalToString(returned))} de ${quantityText(line.quantity)}) · não volta de novo`}
                              </Badge>
                            ) : exhausted ? (
                              <div className="flex flex-wrap items-center gap-2">
                                <Badge tone="warning">
                                  {`já trocada (${quantityText(decimalToString(returned))} de ${quantityText(line.quantity)})`}
                                </Badge>
                                {/*
                                 * O botão aparece para quem está no balcão, e
                                 * não só para quem pode liberar: quem digita a
                                 * senha é o gerente, na sessão do trocador
                                 * (ADR-0026). Esconder daqui esconderia a saída
                                 * de quem precisa chamá-lo.
                                 */}
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="secondary"
                                  onClick={() => {
                                    setReleasing(line.id)
                                  }}
                                >
                                  Liberar mesmo assim
                                </Button>
                              </div>
                            ) : (
                              <QuantityStepper
                                label={`Quantidade devolvida de ${line.description}`}
                                value={draft.quantity}
                                max={returnable}
                                onChange={(value) => {
                                  patchLine(line.id, { quantity: value })
                                }}
                              />
                            )}
                          </div>

                          {overSold ? (
                            <p
                              role="alert"
                              className="text-(length:--text-xs) text-(--danger)"
                            >
                              {returned.units > 0n
                                ? `Desta peça ainda ${compare(returnable, ONE) === 0 ? 'pode voltar 1' : `podem voltar ${quantityText(decimalToString(returnable))}`}: ${quantityText(decimalToString(returned))} de ${quantityText(line.quantity)} já voltaram.`
                                : `Foram vendidas ${quantityText(line.quantity)}.`}
                            </p>
                          ) : null}

                          {released ? (
                            <p className="text-(length:--text-xs) text-(--text-muted)">
                              O gerente liberou esta peça para voltar de novo.
                            </p>
                          ) : null}

                          {marked ? (
                            <div className="flex flex-col gap-2">
                              {/*
                               * O motivo DESTA peça (F178).
                               *
                               * Só quando há mais de uma peça voltando: com uma
                               * só, a pergunta do cabeçalho JÁ é a da peça, e
                               * repetir os mesmos cinco botões logo abaixo deles
                               * é ruído — o trocador teria de escolher entre dois
                               * grupos idênticos. A nota com uma tela com defeito
                               * e outra que não serviu é o caso que pede os dois.
                               */}
                              {returnedLines.length > 1 ? (
                                <Field label={`Motivo de ${line.description}`}>
                                  {() => (
                                    <ExchangeReasonPicker
                                      label={`Motivo de ${line.description}`}
                                      value={lineReason}
                                      onValueChange={(code) => {
                                        patchLine(line.id, {
                                          reasonCode: code,
                                          // Defeito pede o que houve (F178).
                                          ...(code === 'defeito'
                                            ? { noteOpen: true }
                                            : {}),
                                        })
                                      }}
                                    />
                                  )}
                                </Field>
                              ) : null}
                              <div className="flex flex-wrap items-center gap-2">
                                {lineReason === 'outro' ? (
                                  <CheckboxField
                                    label="Volta ao estoque"
                                    checked={draft.restock}
                                    onCheckedChange={(checked) => {
                                      patchLine(line.id, {
                                        restock: checked === true,
                                      })
                                    }}
                                  />
                                ) : lineReason === null ? (
                                  <span className="text-(length:--text-xs) text-(--text-muted)">
                                    O motivo diz se volta ao estoque
                                  </span>
                                ) : (
                                  <span
                                    className={cn(
                                      'rounded-(--radius-sm) bg-(--surface-sunken) px-2 py-1 text-(length:--text-xs) font-(--weight-medium)',
                                      backToStock(draft)
                                        ? 'text-(--success)'
                                        : 'text-(--warning)'
                                    )}
                                  >
                                    {backToStock(draft)
                                      ? 'Volta ao estoque'
                                      : 'Com defeito, não volta ao estoque'}
                                  </span>
                                )}
                                {draft.noteOpen ? null : (
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => {
                                      patchLine(line.id, { noteOpen: true })
                                    }}
                                  >
                                    + observação
                                  </Button>
                                )}
                              </div>
                              {draft.noteOpen ? (
                                <Input
                                  aria-label={`Observação de ${line.description}`}
                                  value={draft.note}
                                  placeholder={
                                    lineReason === 'defeito'
                                      ? 'o que deu de errado? ex.: parou o touch'
                                      : 'ex.: tela trincada, sem caixa'
                                  }
                                  onChange={(event) => {
                                    patchLine(line.id, {
                                      note: event.target.value,
                                    })
                                  }}
                                />
                              ) : null}
                            </div>
                          ) : null}
                        </li>
                      )
                    }
                  )}
                </ul>
              </section>
            ) : null}

            {/* 3. O que leva */}
            {step === 'outgoing' ? (
              <section className="flex flex-col gap-3">
                <Field
                  label="Produto que o cliente leva"
                  hint="Bipe o código de barras ou busque pelo nome. Enter adiciona."
                >
                  {(field) => (
                    <SearchInput
                      {...field}
                      autoFocus
                      wrapperClassName="min-w-0"
                      value={productSearch}
                      placeholder="Nome, código ou código de barras"
                      role="combobox"
                      aria-expanded={productResults.length > 0}
                      aria-controls={productListId}
                      aria-activedescendant={
                        productHighlight.index < 0
                          ? undefined
                          : `${productListId}-${String(productHighlight.index)}`
                      }
                      onKeyDown={productHighlight.onKeyDown}
                      onChange={(event) => {
                        setProductSearch(event.target.value)
                        productHighlight.setIndex(0)
                      }}
                    />
                  )}
                </Field>

                {productTerm.length < 2 ? null : products.isError ? (
                  <InlineAlert tone="danger">
                    Não foi possível buscar os produtos.
                  </InlineAlert>
                ) : products.isPending ? (
                  <p className="flex items-center gap-2 text-(length:--text-sm) text-(--text-muted)">
                    <Spinner /> Buscando produtos…
                  </p>
                ) : productResults.length === 0 ? (
                  <p className="text-(length:--text-sm) text-(--text-muted)">
                    {`Nenhum produto ativo com “${productTerm}”.`}
                  </p>
                ) : (
                  <ul
                    id={productListId}
                    role="listbox"
                    aria-label="Produtos encontrados"
                    className="flex flex-col gap-1"
                  >
                    {productResults.map((option, index) => (
                      <li
                        key={option.key}
                        id={`${productListId}-${String(index)}`}
                        role="option"
                        aria-selected={index === productHighlight.index}
                        onMouseEnter={() => {
                          productHighlight.setIndex(index)
                        }}
                        onClick={() => {
                          addProduct(option)
                        }}
                        className={cn(
                          'flex cursor-pointer items-center justify-between gap-3 rounded-(--radius-md) border px-3 py-1.5',
                          index === productHighlight.index
                            ? 'border-(--brand-primary) bg-(--surface-sunken)'
                            : 'border-(--border-default)'
                        )}
                      >
                        <span className="truncate">{option.label}</span>
                        <span className="shrink-0 font-(--font-mono) text-(--text-secondary)">
                          {option.product.price?.basePrice == null
                            ? 'sem preço'
                            : money(
                                option.product.price.basePrice,
                                UNIT_PRICE_SCALE
                              )}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}

                {outgoing.length === 0 ? (
                  <p className="rounded-(--radius-md) border border-dashed border-(--border-default) px-3 py-4 text-center text-(length:--text-sm) text-(--text-muted)">
                    Nada escolhido ainda.
                  </p>
                ) : (
                  <ul
                    aria-label="O que o cliente leva"
                    className="flex flex-col gap-2"
                  >
                    {outgoing.map((item) => (
                      <li
                        key={item.variantId}
                        className={cn(
                          'flex flex-wrap items-center justify-between gap-3 rounded-(--radius-md) border p-3',
                          item.unitPrice === null
                            ? 'border-(--warning)'
                            : 'border-(--border-default)'
                        )}
                      >
                        <span className="flex min-w-0 flex-col">
                          <span className="font-(--weight-medium)">
                            {item.name}
                          </span>
                          <span className="text-(length:--text-xs) text-(--text-muted)">
                            {item.unitPrice === null
                              ? 'sem preço cadastrado'
                              : `${money(item.unitPrice, UNIT_PRICE_SCALE)} cada`}
                          </span>
                        </span>
                        <span className="flex items-center gap-2">
                          <QuantityStepper
                            label={`Quantidade levada de ${item.name}`}
                            value={item.quantity}
                            max={roomFor(item)}
                            onChange={(value) => {
                              setOutgoing((current) =>
                                current.map((entry) =>
                                  entry.variantId === item.variantId
                                    ? { ...entry, quantity: value }
                                    : entry
                                )
                              )
                            }}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label={`Tirar ${item.name}`}
                            leadingIcon={
                              <Trash2
                                aria-hidden="true"
                                className="h-3.5 w-3.5"
                              />
                            }
                            onClick={() => {
                              setOutgoing((current) =>
                                current.filter(
                                  (entry) => entry.variantId !== item.variantId
                                )
                              )
                            }}
                          />
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ) : null}

            {/* 4. Conferir */}
            {step === 'review' && document !== undefined ? (
              <section className="flex flex-col gap-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="flex flex-col gap-1 rounded-(--radius-md) border border-(--border-default) p-3">
                    <h3 className="text-(length:--text-xs) font-(--weight-semibold) uppercase tracking-wide text-(--text-muted)">
                      Volta
                    </h3>
                    <ul className="flex flex-col gap-1 text-(length:--text-sm)">
                      {returnedLines.map((entry) => (
                        <li
                          key={entry.line.id}
                          className="flex justify-between gap-2"
                        >
                          <span className="min-w-0">
                            {`${quantityText(decimalToString(entry.quantity))} × ${entry.line.description}`}
                            <span className="block text-(length:--text-xs) text-(--text-muted)">
                              {backToStock(entry.draft)
                                ? 'volta ao estoque'
                                : 'não volta ao estoque'}
                              {entry.draft.note.trim() === ''
                                ? ''
                                : ` · ${entry.draft.note.trim()}`}
                            </span>
                          </span>
                          <span className="shrink-0 font-(--font-mono) tabular-nums">
                            {formatMoney(
                              lineTotal(
                                entry.quantity,
                                paidUnitPrice(entry.line)
                              )
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {kind === 'exchange' ? (
                    <div className="flex flex-col gap-1 rounded-(--radius-md) border border-(--border-default) p-3">
                      <h3 className="text-(length:--text-xs) font-(--weight-semibold) uppercase tracking-wide text-(--text-muted)">
                        Leva
                      </h3>
                      <ul className="flex flex-col gap-1 text-(length:--text-sm)">
                        {outgoingChecks.map(({ item, quantity }) => (
                          <li
                            key={item.variantId}
                            className="flex justify-between gap-2"
                          >
                            <span className="min-w-0">{`${item.quantity} × ${item.name}`}</span>
                            <span className="shrink-0 font-(--font-mono) tabular-nums">
                              {quantity === null || item.unitPrice === null
                                ? '—'
                                : formatMoney(
                                    lineTotal(
                                      quantity,
                                      fromString(
                                        item.unitPrice,
                                        UNIT_PRICE_SCALE
                                      )
                                    )
                                  )}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>

                <p
                  aria-live="polite"
                  className={cn(
                    'text-center text-(length:--text-xl) font-(--weight-semibold)',
                    resultTone
                  )}
                >
                  {resultText}
                </p>

                <Field label="Observação" hint="Sai no comprovante.">
                  {(field) => (
                    <Textarea
                      {...field}
                      rows={2}
                      value={note}
                      maxLength={500}
                      onChange={(event) => {
                        setNote(event.target.value)
                      }}
                    />
                  )}
                </Field>
              </section>
            ) : null}

            {error === null ? null : (
              <InlineAlert tone="danger">{error}</InlineAlert>
            )}

            <DialogFooter className="items-center justify-between">
              <div className="flex min-w-0 flex-col text-(length:--text-sm)">
                {step === 'sale' ? (
                  <span className="text-(--text-muted)">
                    {blocker ?? 'Venda escolhida.'}
                  </span>
                ) : (
                  <>
                    <span className="text-(--text-secondary)">
                      {`Volta ${formatMoney(returnedTotal)}`}
                      {kind === 'exchange'
                        ? ` · Leva ${formatMoney(outgoingTotal)}`
                        : ''}
                      {' · '}
                      <strong className={resultTone}>{resultText}</strong>
                    </span>
                    {blocker === null ? null : (
                      <span className="text-(length:--text-xs) text-(--text-muted)">
                        {blocker}
                      </span>
                    )}
                  </>
                )}
              </div>
              <div className="flex gap-2">
                {stepIndex === 0 ||
                (steps[stepIndex - 1]?.id === 'sale' && request !== null) ? (
                  <Button type="button" variant="secondary" onClick={onClose}>
                    Cancelar
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="secondary"
                    leadingIcon={
                      <ArrowLeft aria-hidden="true" className="h-4 w-4" />
                    }
                    onClick={back}
                  >
                    Voltar
                  </Button>
                )}
                {step === 'review' ? (
                  <Button
                    type="submit"
                    variant="primary"
                    loading={create.isPending}
                  >
                    Concluir e imprimir
                  </Button>
                ) : (
                  /*
                   * `aria-disabled`, e não `disabled`.
                   *
                   * Um formulário cujo botão de enviar está `disabled` IGNORA o
                   * Enter nos campos (a submissão implícita do HTML). Era o bipe
                   * do código de barras na etapa "O que leva" sem fazer nada: o
                   * Continuar estava apagado porque ainda não havia produto, e
                   * o Enter que adicionaria o produto nunca chegava. A trava
                   * continua no `onSubmit`, e o motivo continua escrito ao lado.
                   */
                  <Button
                    type="submit"
                    variant="primary"
                    aria-disabled={blocker !== null}
                    className={blocker === null ? undefined : 'opacity-60'}
                    trailingIcon={
                      <ArrowRight aria-hidden="true" className="h-4 w-4" />
                    }
                  >
                    Continuar
                  </Button>
                )}
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/*
       * A senha do gerente para a peça que já voltou (F178).
       *
       * A mesma tela da troca sem venda (F148): o gerente escreve o porquê antes
       * da senha, e o "sim" fica preso à venda E às linhas que ele viu. As
       * liberadas se somam, porque a nota com duas telas já trocadas se resolve
       * numa conversa só.
       */}
      {releasing !== null && document !== undefined ? (
        <AuthorisationDialog
          title="Liberar peça já trocada"
          description="Esta peça já voltou nesta venda. Confira o comprovante da troca anterior antes de liberar."
          action="exchange.repeat_return"
          targetType="sales_document"
          targetId={document.id}
          detail={{ saleLineIds: [...new Set([...releasedLines, releasing])] }}
          summary={
            <p className="text-(length:--text-sm) text-(--text-muted)">
              {lines.find((line) => line.id === releasing)?.description ??
                'Peça da venda'}
            </p>
          }
          onAuthorised={(authorisationId) => {
            setRepeatAuthorisationId(authorisationId)
            setReleasedLines((current) =>
              current.includes(releasing) ? current : [...current, releasing]
            )
            setReleasing(null)
          }}
          onClose={() => {
            setReleasing(null)
          }}
        />
      ) : null}
    </>
  )
}
